import { useState } from "react";
import { IoNotificationsOutline } from "react-icons/io5";
import { PageShell, PageHeader } from "../../../Components/shared";
import { TextInput, Textarea, NumberInput, CustomDropdown } from "../../../Components/atoms";
import { useBroadcastPushMutation } from "../../../store";
import useAuth from "../../../Hooks/useAuth";
import { useAlert } from "../../../context/AlertContext";

const TITLE_MAX = 60;
const BODY_MAX = 160;

const SCREEN_OPTIONS = [
  { id: "", label: "Ana Sayfa", description: "Bildirime dokunan kullanıcı ana sayfaya yönlendirilir" },
  { id: "campaigns", label: "Kampanyalar" },
  { id: "duyurular", label: "Duyurular" },
  { id: "wallet-transactions", label: "Cüzdan Hareketleri" },
  { id: "topup", label: "Bakiye Yükleme" },
];

const PLATFORM_OPTIONS = [
  { id: "", label: "Tüm Platformlar" },
  { id: "android", label: "Yalnızca Android" },
  { id: "ios", label: "Yalnızca iOS" },
];

const AUDIENCE_OPTIONS = [
  { id: "all", label: "Tüm Kullanıcılar" },
  {
    id: "inactive",
    label: "Uzun Süredir Alışveriş Yapmayanlar",
    description: "Belirtilen gün sayısı boyunca içecek almamış kullanıcılar",
  },
];

export default function PushBroadcastPage() {
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const { showSuccess, showError } = useAlert();
  const [broadcastPush, { isLoading }] = useBroadcastPushMutation();

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [screen, setScreen] = useState(SCREEN_OPTIONS[0]);
  const [platform, setPlatform] = useState(PLATFORM_OPTIONS[0]);
  const [audience, setAudience] = useState(AUDIENCE_OPTIONS[0]);
  const [inactiveDays, setInactiveDays] = useState(14);
  const [scheduled, setScheduled] = useState(false);
  const [sendAt, setSendAt] = useState("");
  const [errors, setErrors] = useState({});

  const validate = () => {
    const next = {};
    if (!title.trim()) next.title = "Başlık zorunludur.";
    else if (title.length > TITLE_MAX) next.title = `Başlık en fazla ${TITLE_MAX} karakter olabilir.`;
    if (!body.trim()) next.body = "Mesaj zorunludur.";
    else if (body.length > BODY_MAX) next.body = `Mesaj en fazla ${BODY_MAX} karakter olabilir.`;
    if (audience.id === "inactive" && (!inactiveDays || inactiveDays < 1)) {
      next.inactiveDays = "Geçerli bir gün sayısı girin.";
    }
    if (scheduled) {
      if (!sendAt) next.sendAt = "Gönderim zamanı seçin.";
      else if (new Date(sendAt) <= new Date()) next.sendAt = "Gönderim zamanı gelecekte olmalıdır.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const resetForm = () => {
    setTitle("");
    setBody("");
    setScreen(SCREEN_OPTIONS[0]);
    setPlatform(PLATFORM_OPTIONS[0]);
    setAudience(AUDIENCE_OPTIONS[0]);
    setInactiveDays(14);
    setScheduled(false);
    setSendAt("");
    setErrors({});
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const audienceText =
      audience.id === "inactive"
        ? `${inactiveDays} gündür alışveriş yapmayan kullanıcılara`
        : "tüm kullanıcılara";
    const timeText = scheduled
      ? `${new Date(sendAt).toLocaleString("tr-TR")} tarihinde`
      : "hemen";
    if (!window.confirm(`Bildirim ${audienceText} ${timeText} gönderilecek. Onaylıyor musunuz?`)) {
      return;
    }

    try {
      const payload = {
        token,
        title: title.trim(),
        body: body.trim(),
      };
      if (screen.id) payload.screen = screen.id;
      if (platform.id) payload.platform = platform.id;
      if (audience.id === "inactive") payload.inactiveDays = Number(inactiveDays);
      if (scheduled) payload.sendAt = new Date(sendAt).toISOString();

      const res = await broadcastPush(payload).unwrap();

      if (scheduled) {
        showSuccess(`Bildirim ${new Date(sendAt).toLocaleString("tr-TR")} için planlandı.`);
      } else {
        showSuccess(`Bildirim ${res?.data?.sentTokens ?? 0} cihaza gönderildi.`);
      }
      resetForm();
    } catch (err) {
      showError(err?.data?.message || "Bildirim gönderilemedi. Lütfen tekrar deneyin.");
    }
  };

  return (
    <PageShell>
      <div className="px-3 py-2.5 sm:p-4 md:p-6 border-b border-gray-100">
        <PageHeader
          icon={IoNotificationsOutline}
          iconColor="indigo"
          title="Bildirim Gönder"
          subtitle="Mobil uygulama kullanıcılarına push bildirimi gönderin"
        />
      </div>

      <div className="p-3 sm:p-4 md:p-6 max-w-3xl">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-6 space-y-5">
          {/* Başlık */}
          <div>
            <TextInput
              label="Başlık"
              required
              placeholder="Örn. Yeni Kampanya"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              error={!!errors.title}
              errorMessage={errors.title}
            />
            <p className="mt-1 text-xs text-gray-400 text-right">
              {title.length}/{TITLE_MAX}
            </p>
          </div>

          {/* Mesaj */}
          <div>
            <Textarea
              label="Mesaj"
              required
              rows={3}
              placeholder="Örn. Bu hafta sonu ikinci içecek hediye."
              value={body}
              onChange={(e) => setBody(e.target.value)}
              error={!!errors.body}
              errorMessage={errors.body}
            />
            <p className="mt-1 text-xs text-gray-400 text-right">
              {body.length}/{BODY_MAX}
            </p>
          </div>

          {/* Yazım kuralı notu */}
          <div className="rounded-xl bg-gray-50 border border-gray-100 px-4 py-3 text-xs text-gray-500 leading-relaxed">
            Yazım kuralları: Emoji kullanmayın. "Siz" hitabıyla, kısa bir başlık ve
            bilgilendirici bir mesaj yazın. Örnek — Başlık: "Bakiye Yüklendi",
            Mesaj: "200 TL bakiyenize eklendi."
          </div>

          {/* Hedefleme */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <CustomDropdown
              label="Hedef Ekran"
              value={screen}
              onChange={setScreen}
              options={SCREEN_OPTIONS}
              showSearch={false}
            />
            <CustomDropdown
              label="Platform"
              value={platform}
              onChange={setPlatform}
              options={PLATFORM_OPTIONS}
              showSearch={false}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <CustomDropdown
              label="Hedef Kitle"
              value={audience}
              onChange={setAudience}
              options={AUDIENCE_OPTIONS}
              showSearch={false}
            />
            {audience.id === "inactive" && (
              <NumberInput
                label="Kaç gündür alışveriş yok?"
                value={inactiveDays}
                onChange={(e) => setInactiveDays(e.target.value)}
                error={!!errors.inactiveDays}
                errorMessage={errors.inactiveDays}
              />
            )}
          </div>

          {/* Gönderim zamanı */}
          <div className="space-y-3">
            <label className="block text-sm font-semibold text-gray-700">
              Gönderim Zamanı
            </label>
            <div className="flex items-center gap-6">
              <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="radio"
                  checked={!scheduled}
                  onChange={() => setScheduled(false)}
                  className="accent-indigo-600"
                />
                Hemen gönder
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="radio"
                  checked={scheduled}
                  onChange={() => setScheduled(true)}
                  className="accent-indigo-600"
                />
                Zamanla
              </label>
            </div>
            {scheduled && (
              <div>
                <input
                  type="datetime-local"
                  value={sendAt}
                  onChange={(e) => setSendAt(e.target.value)}
                  className={`w-full sm:w-64 px-4 py-3 bg-white border rounded-2xl text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 shadow-sm ${
                    errors.sendAt ? "border-red-300" : "border-gray-200"
                  }`}
                />
                {errors.sendAt && (
                  <p className="mt-1 text-xs text-red-500">{errors.sendAt}</p>
                )}
              </div>
            )}
          </div>

          {/* Gönder */}
          <div className="pt-2 flex justify-end">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isLoading}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-60 transition-colors"
            >
              <IoNotificationsOutline className="w-4 h-4" />
              {isLoading
                ? "Gönderiliyor..."
                : scheduled
                ? "Bildirimi Zamanla"
                : "Bildirimi Gönder"}
            </button>
          </div>
        </div>
      </div>
    </PageShell>
  );
}
